/**
 * Content items collection — typed CRUD on top of the generic store.
 *
 * Wraps `db` so the Content Engine deals in `ContentItem` records and so
 * id minting (sequential `CNT-###`) and timestamp stamping live in one
 * place. Modelled directly on `tickets.ts`. When we cut over to Firestore
 * the only thing that changes is the adapter selection in `store.ts`.
 *
 * Pipeline semantics (Idea → Draft → Scheduled → Published) are PURE and
 * live in src/lib/content/pipeline.ts — this module only persists.
 */

import { db, nowIso } from './store';
import type { ContentItem, ContentItemStatus, ContentItemType } from './types';

export async function listContentItems(): Promise<ContentItem[]> {
  return db.list('contentItems');
}

export async function getContentItem(id: string): Promise<ContentItem | null> {
  return db.get('contentItems', id);
}

export function subscribeContentItems(
  fn: (records: ContentItem[]) => void,
): () => void {
  return db.subscribe('contentItems', fn);
}

/** Mint the next sequential id ("CNT-001", "CNT-002", …). */
async function nextContentId(): Promise<string> {
  const all = await db.list('contentItems');
  let max = 0;
  for (const c of all) {
    const m = /^CNT-(\d+)$/.exec(c.id);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `CNT-${String(max + 1).padStart(3, '0')}`;
}

export interface NewContentItemInput {
  title: string;
  type: ContentItemType;
  status?: ContentItemStatus;
  body?: string;
  score?: number | null;
  /** Honest provenance of `score`: true = local simulation, not the engine. */
  scoreSimulated?: boolean;
  scheduledFor?: string | null;
  templateId?: string | null;
}

export async function createContentItem(
  input: NewContentItemInput,
): Promise<ContentItem> {
  const ts = nowIso();
  const id = await nextContentId();
  const record: ContentItem = {
    id,
    title: input.title,
    type: input.type,
    status: input.status ?? 'idea',
    body: input.body ?? '',
    score: input.score ?? null,
    // Omit (not undefined) when unset — adapters may reject undefined values.
    ...(input.scoreSimulated !== undefined ? { scoreSimulated: input.scoreSimulated } : {}),
    scheduledFor: input.scheduledFor ?? null,
    templateId: input.templateId ?? null,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('contentItems', record);
  return record;
}

/** Patch a content item. Re-stamps `updatedAt`. */
export async function updateContentItem(
  id: string,
  patch: Partial<Omit<ContentItem, 'id' | 'createdAt'>>,
): Promise<ContentItem> {
  const existing = await db.get('contentItems', id);
  if (!existing) throw new Error(`No content item ${id}`);
  return db.update('contentItems', id, { ...patch, updatedAt: nowIso() });
}

/** Move an item to a pipeline stage. */
export async function setContentStatus(
  id: string,
  status: ContentItemStatus,
): Promise<ContentItem> {
  return updateContentItem(id, { status });
}

export async function deleteContentItem(id: string): Promise<void> {
  await db.remove('contentItems', id);
}

/**
 * First-run seed so the kanban demonstrates itself: one item per stage.
 * Deliberately small — real work replaces it immediately. Idempotent.
 */
const SEED_CONTENT_ITEMS: ContentItem[] = [
  {
    id: 'CNT-001',
    title: 'Why we killed our onboarding call',
    type: 'linkedin-post',
    status: 'idea',
    body: '',
    score: null,
    scheduledFor: null,
    templateId: null,
    createdAt: '2026-01-05T09:00:00.000Z',
    updatedAt: '2026-01-05T09:00:00.000Z',
  },
  {
    id: 'CNT-002',
    title: '3 pricing mistakes thread',
    type: 'x-thread',
    status: 'draft',
    body: 'I raised prices 40% and churn went DOWN.\n\nHere\'s the playbook:\n\n1/ Anchor on outcomes, not hours.\n\n2/ Grandfather loyal accounts for 90 days.\n\n3/ Tell customers WHY — in one honest paragraph.',
    score: null,
    scheduledFor: null,
    templateId: 'x-hook-thread',
    createdAt: '2026-01-06T09:00:00.000Z',
    updatedAt: '2026-01-08T09:00:00.000Z',
  },
  {
    id: 'CNT-003',
    title: 'Q1 customer teardown blog',
    type: 'blog',
    status: 'scheduled',
    body: '# How one operator cut response time 60%\n\nThe result first: 60% faster replies in 3 weeks.\n\n## The setup\nA 4-person team drowning in a shared inbox.',
    score: null,
    scheduledFor: '2026-03-02T09:00:00.000Z',
    templateId: 'blog-teardown',
    createdAt: '2026-01-07T09:00:00.000Z',
    updatedAt: '2026-01-09T09:00:00.000Z',
  },
  {
    id: 'CNT-004',
    title: 'Launch announcement email',
    type: 'email',
    status: 'published',
    body: 'Subject: The feature you asked for is live\n\nHi there,\n\n47 of you asked for saved views. They shipped this morning.\n\nReply if anything feels off — I read every one.\n\nTrent',
    score: null,
    scheduledFor: null,
    templateId: null,
    createdAt: '2026-01-02T09:00:00.000Z',
    updatedAt: '2026-01-04T09:00:00.000Z',
  },
];

/** Seed the contentItems collection on first run. Idempotent. */
export async function bootstrapContentItemsIfEmpty(): Promise<void> {
  const existing = await db.list('contentItems');
  if (existing.length > 0) return;
  await db.replaceAll('contentItems', SEED_CONTENT_ITEMS);
}
